import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Search,
  GraduationCap,
  ScrollText,
  BadgeCheck,
} from "lucide-react";
import { ABROAD_FILTERS, ABROAD_CATEGORY_STYLES, ABROAD_CATALOG } from "@/data/content";
import { useEnquiryPopup } from "@/context/EnquiryPopupContext";

const PROGRAM_ICONS = { GraduationCap, ScrollText, BadgeCheck };

export default function StudyAbroad() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const { openPopup } = useEnquiryPopup();

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const applyFor = (item) => {
    if (item.category === "Countries") openPopup({ course: `Study in ${item.title}` });
    else openPopup({ course: item.title });
  };

  const selectCard = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ABROAD_CATALOG.filter((item) => {
      const matchesFilter = activeFilter === "All" || item.category === activeFilter;
      if (!matchesFilter) return false;
      if (!q) return true;
      const haystack = [item.title, item.subtitle, item.desc, ...(item.tags || [])].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [activeFilter, query]);

  return (
    <section id="abroad" data-testid="abroad-section" className="section bg-[#F8F9FA] relative">
      <div className="container-x">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#0A3161]/15 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#B31942]">
            Study Abroad Consulting
          </div>
          <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-slate-900" data-testid="abroad-heading">
            Study Abroad — Your Gateway to <span className="text-[#B31942]">Global Education</span>.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Choose from destinations, programs, test prep and scholarships designed to get you
            admitted, funded and settled — anywhere in the world.
          </p>
        </div>

        {/* Search + Filters */}
        <div className="mt-10 pb-6 border-b border-black/10 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative w-full lg:w-72 shrink-0">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search countries, tests, aid..."
              data-testid="abroad-search"
              className="w-full rounded-xl border border-black/10 bg-white pl-10 pr-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#B31942]/50 focus:ring-2 focus:ring-[#B31942]/10 transition-colors"
            />
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            {ABROAD_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                data-testid={`abroad-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                  activeFilter === f
                    ? "bg-[#B31942] text-white border-[#B31942]"
                    : "bg-white text-slate-700 border-black/10 hover:border-[#B31942]/40"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Card grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {results.map((item, i) => {
            const Icon = item.icon ? PROGRAM_ICONS[item.icon] : null;
            const isSelected = selectedId === item.id;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                animate={{ scale: isSelected ? 1.035 : 1 }}
                transition={{
                  opacity: { duration: 0.4, delay: (i % 3) * 0.06 },
                  y: { duration: 0.4, delay: (i % 3) * 0.06 },
                  scale: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
                }}
                onClick={() => selectCard(item.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    selectCard(item.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                data-testid={`abroad-card-${item.id}`}
                data-selected={isSelected || undefined}
                className={`relative rounded-2xl bg-white p-6 cursor-pointer select-none outline-none transition-[box-shadow,border-color] duration-300 ease-out focus-visible:ring-2 focus-visible:ring-[#940835]/40 focus-visible:ring-offset-2 ${
                  isSelected
                    ? "border-2 border-[#940835] card-selected-glow"
                    : item.featured
                    ? "border-2 border-[#B31942]/25 ring-2 ring-[#B31942]/10 soft-shadow hover:soft-shadow-lg"
                    : "border border-black/5 soft-shadow hover:soft-shadow-lg"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#B31942]/10 px-3 py-1 text-xs font-bold text-[#B31942]">
                    {item.badge}
                  </span>
                  <span className="text-xs font-medium text-slate-400 whitespace-nowrap">{item.info}</span>
                </div>

                <div className="mt-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${ABROAD_CATEGORY_STYLES[item.category]}`}>
                    {item.category}
                  </span>
                </div>

                <h3 className="mt-3 font-display text-xl font-bold text-slate-900 flex items-center gap-2">
                  {item.flag && <span aria-hidden>{item.flag}</span>}
                  {Icon && <Icon className="h-5 w-5 text-[#0A3161]" aria-hidden />}
                  {item.title}
                </h3>
                <div className="mt-1 text-xs text-slate-400">{item.subtitle}</div>

                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.desc}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#F8F9FA] text-slate-700 border border-black/5">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollTo("#contact");
                    }}
                    data-testid={`abroad-details-${item.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-[#B31942] text-[#B31942] px-4 py-2 text-sm font-semibold hover:bg-[#B31942]/5 transition-colors"
                  >
                    View Details <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      applyFor(item);
                    }}
                    data-testid={`abroad-primary-${item.id}`}
                    className="flex-1 rounded-full bg-[#B31942] text-white px-4 py-2 text-sm font-semibold hover:bg-[#940835] shadow-[0_10px_24px_rgba(179,25,66,0.22)] transition-colors"
                  >
                    Enquire Now
                  </button>
                </div>
              </motion.div>
            );
          })}

          {results.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-500">
              No matches yet — try a different search or filter.
            </div>
          )}
        </div>

        {/* CTA banner */}
        <div className="mt-14 rounded-2xl bg-white border border-black/5 soft-shadow px-6 py-10 md:px-12 text-center">
          <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
            Not Sure Which Destination Is Right for You?
          </h3>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Our counselors will help you pick the right country, program and test — based on your
            goals and budget — for free.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => openPopup()}
              data-testid="abroad-cta-apply"
              className="inline-flex items-center gap-2 rounded-full bg-[#B31942] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#940835] shadow-[0_10px_24px_rgba(179,25,66,0.28)] transition-colors"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="abroad-cta-counselor"
              className="inline-flex items-center gap-2 rounded-full border border-[#0A3161]/20 text-[#0A3161] px-5 py-2.5 text-sm font-semibold hover:bg-[#0A3161]/5 transition-colors"
            >
              Talk to a Counselor
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
